// Очередь материала — экран, на котором работает штаб.
//
// Пайплайн ручной: у клиента нет API, поэтому «отгружено» и «принято» — это
// факты, которые человек фиксирует здесь ПОСЛЕ действия в реальном мире.
// Экран построен вокруг четырёх стопок, а не вокруг одного списка со
// статусом-колонкой: вопрос «что сейчас нужно сделать» должен отвечаться
// вкладкой, а не глазами по таблице.
import { useEffect, useState } from 'preact/hooks';
import type { VistaCaptureMove, VistaQueueRow, VistaRejectReason } from '@weern/contracts';
import { api, getToken, type VistaMeRes as Me } from '../api';
import { invalidate, prefetch, useResource } from '../cache';
import type { T } from '../i18n';
import { PlayerModal } from '../components/PlayerModal';
import { VerdictsModal } from '../components/VerdictsModal';

/** A 'rejected' clip with bytes=0 was discarded before any footage landed
 *  (handleCaptureAbort — a deliberate cancel or an expired upload) — there is
 *  nothing in R2 to play or download. 'recording' never reaches this screen
 *  (handleCaptures filters it out), but the check stays here too since this
 *  same test decides whether the "смотреть" button shows. */
const hasPlayableFile = (r: Pick<VistaQueueRow, 'status' | 'bytes'>): boolean =>
  r.status !== 'recording' && !(r.status === 'rejected' && r.bytes === 0);

type Tab = 'needs_voiceover' | 'voiced' | 'delivered' | 'pending' | 'accepted' | 'rejected' | 'quarantine';

// Стопки идут в порядке движения материала. «Принято» здесь не для действий, а
// чтобы принятое вообще было где посмотреть: без этой вкладки клип после
// вердикта исчезал из очереди совсем, и проверить, что именно приняли, было
// негде.
// Карантин в конце — гейт целостности забраковал запись, но залитое
// запечатано, а не выброшено. Штаб скачивает, смотрит диагностику и решает:
// реабилитировать в озвучку или похоронить.
const TABS: Tab[] = [
  'needs_voiceover',
  'voiced',
  'delivered',
  'pending',
  'accepted',
  'rejected',
  'quarantine',
];

// Вкладки, где штаб делает QA НЕ дожидаясь вердикта клиента — здесь важно
// видеть, когда клип уехал, отдельной колонкой от «Снято» (kids-парность,
// «Просмотр сессий» 11.08).
const SHIPPED_DATE_TABS = new Set<Tab>(['delivered', 'pending', 'accepted']);

// Формулировки — из отказного списка клиента, чтобы то, что видит контрибьютор,
// совпадало с тем, по чему нас судят.
const REJECT_REASONS: VistaRejectReason[] = [
  'integrity',
  'no_narration',
  'weak_narration',
  'hands_out_of_frame',
  'poor_light',
  'motion_blur',
  'repetitive',
  'staged',
  'privacy',
  'duplicate',
  'off_topic',
  'other',
];

/** «10:03 / 14:50 · 20 fps · 76 дропов» из diag_json карантина. */
function fmtDiag(raw: string): string {
  try {
    const d = JSON.parse(raw) as {
      realVideoSeconds?: number; wallClockSeconds?: number;
      encodedFrames?: number; droppedFrames?: number;
    };
    const parts: string[] = [];
    if (d.realVideoSeconds != null && d.wallClockSeconds != null)
      parts.push(`${fmtDur(Math.round(d.realVideoSeconds))} / ${fmtDur(Math.round(d.wallClockSeconds))}`);
    if (d.encodedFrames != null && d.wallClockSeconds)
      parts.push(`${Math.round(d.encodedFrames / d.wallClockSeconds)} fps`);
    if (d.droppedFrames) parts.push(`${d.droppedFrames} drops`);
    return parts.join(' · ');
  } catch {
    return '';
  }
}

const fmtDur = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};
const fmtMB = (b: number): string => (b > 0 ? `${(b / (1024 * 1024)).toFixed(0)} МБ` : '—');
const fmtDate = (ms: number | null): string =>
  ms ? new Date(ms).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

export function QueuePage({ me, t }: { me: Me; t: T }) {
  // Вкладку можно назвать в адресе (#/queue/pending) — так цифры на дашборде
  // становятся кликабельными: человек видит «12 ждут вердикт», нажимает и
  // попадает ровно в эту стопку, а не ищет её вкладками руками.
  const [tab, setTab] = useState<Tab>(() => {
    const want = location.hash.split('/')[2]?.split('?')[0];
    return want && (TABS as string[]).includes(want) ? (want as Tab) : 'needs_voiceover';
  });
  // Адрес и вкладка идут вместе: «назад» в браузере возвращает предыдущую
  // стопку, а ссылку на стопку можно кинуть коллеге.
  useEffect(() => {
    const want = `#/queue/${tab}`;
    if (location.hash !== want) history.replaceState(null, '', want);
  }, [tab]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [verdictsOpen, setVerdictsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [rejectFor, setRejectFor] = useState<string[] | null>(null);
  const [playFor, setPlayFor] = useState<VistaQueueRow | null>(null);

  // Фильтры: серверные, а не клиентские — стопка режется лимитом 300 строк,
  // и клиентский фильтр над обрезанной выдачей молча теряет то, что не
  // влезло в первые 300 (kids-парность, «Просмотр сессий» 11.08).
  const [contributorId, setContributorId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState(''); // живое поле
  const [qDeb, setQDeb] = useState(''); // то, что реально ушло в запрос

  // Поиск дебаунсим: каждый символ — это доп. SELECT по D1.
  useEffect(() => {
    const id = setTimeout(() => setQDeb(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  const isManager = me.role === 'manager';

  const filters = { status: tab, contributorId: contributorId || undefined, from: from || undefined, to: to || undefined, q: qDeb || undefined, limit: 300 };
  // Ключ кеша несёт фильтры — смена дат/поиска обязана бить в свой собственный
  // слот, а не показывать чужую выдачу, пока идёт фоновый рефетч.
  const cacheKey = `captures:${tab}:${contributorId}:${from}:${to}:${qDeb}`;

  // Данные из кеша рисуются сразу, свежие подъезжают в фоне — переключение
  // стопок должно быть мгновенным, здесь человек щёлкает вкладками весь день.
  const res = useResource(cacheKey, () => api.captures(filters));
  const rows: VistaQueueRow[] | null = res.data?.rows ?? null;
  // Справочник контрибьюторов для фильтра — приезжает с сервера по вкладке в
  // пределах скоупа (НЕ по текущей отфильтрованной выдаче): иначе после
  // выбора имени в списке остаётся он один и вернуться к «Все» нечем.
  const contributors = res.data?.contributors ?? [];
  const load = async (_t: Tab) => {
    setSelected(new Set());
    await res.refresh();
  };

  // Соседние стопки (без фильтров) прогреваются заранее: первый переход
  // тоже без ожидания. Отфильтрованные комбинации не прогреваем — их
  // сочетаний слишком много, чтобы это того стоило.
  useEffect(() => {
    if (contributorId || from || to || qDeb) return;
    for (const other of TABS) {
      if (other !== tab) {
        prefetch(`captures:${other}::::`, () => api.captures({ status: other, limit: 300 }));
      }
    }
  }, [tab, contributorId, from, to, qDeb]);

  useEffect(() => {
    setSelected(new Set());
  }, [tab, contributorId, from, to, qDeb]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const move = async (mv: VistaCaptureMove, reason?: VistaRejectReason, note?: string) => {
    const ids = rejectFor ?? [...selected];
    if (ids.length === 0) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.moveCaptures({ ids, move: mv, reason, note });
      // Пропущенные показываем всегда: батч, сработавший наполовину, обязан
      // сказать, какая половина — иначе «отгрузил» станет неправдой.
      const parts = [t('q.moved', { n: res.moved })];
      if (res.skipped.length) parts.push(t('q.skipped', { n: res.skipped.length }));
      if (res.accruedCents) parts.push(t('q.accrued', { amount: (res.accruedCents / 100).toFixed(2) }));
      // Отмена начисления при развороте вердикта — отдельной строкой: молча
      // уменьшить чей-то баланс нельзя, человек должен узнать причину.
      if (res.reversedCents) {
        parts.push(t('q.reversed', { amount: (res.reversedCents / 100).toFixed(2) }));
      }
      if (res.unratedIds.length) parts.push(t('q.unrated', { n: res.unratedIds.length }));
      setMsg(parts.join(' · '));
      setRejectFor(null);
      // Перенос меняет ОБЕ стопки — и ту, из которой ушло, и ту, куда пришло,
      // и вместе с ними деньги. Точечное обновление одной вкладки оставило бы
      // соседнюю врать до истечения свежести.
      invalidate('captures:');
      invalidate('balances');
      await load(tab);
    } catch {
      setMsg(t('q.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const uploadVoiced = async (id: string, file: File) => {
    setBusy(true);
    setMsg(null);
    try {
      // Bug fix (vista role audit, 12.08): this read a localStorage key
      // ('vista.token') that nothing else in the app ever writes — the real
      // session lives under 'vista_session' (api.ts TOKEN_KEY). Every voiced
      // upload was going out with no Authorization header and dying on the
      // worker's 401, silently ("Файл не загрузился." — no clue why). Route
      // through getToken() like every other call, which also correctly picks
      // the CONTRIBUTOR token if a staff person is filming under a switched-in
      // contributor cabinet (F3) — this endpoint is admin-gated so that path
      // is theoretical today, but getToken() is the one function that knows
      // which token is currently active and must stay the only source of truth.
      const token = getToken();
      const res = await fetch(`${api.captureFileUrl(id).split('/file')[0]}/voiced`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/octet-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: file,
      });
      if (!res.ok) throw new Error('upload_failed');
      setMsg(t('q.voiced.uploaded'));
      invalidate('captures:');
      await load(tab);
    } catch {
      setMsg(t('q.voiced.failed'));
    } finally {
      setBusy(false);
    }
  };

  const canMoveFrom: Partial<Record<Tab, Array<{ move: VistaCaptureMove; label: string; managerOnly?: boolean }>>> = {
    voiced: [{ move: 'deliver', label: t('q.move.deliver') }],
    delivered: [
      { move: 'pending', label: t('q.tab.pending') },
      { move: 'accept', label: t('q.move.accept'), managerOnly: true },
    ],
    pending: [{ move: 'accept', label: t('q.move.accept'), managerOnly: true }],
    rejected: [{ move: 'requeue', label: t('q.move.requeue') }],
    quarantine: [{ move: 'requeue', label: t('q.move.rehab') }],
  };

  return (
    <>
      <nav class="seg">
        {TABS.map((key) => (
          <button key={key} class={tab === key ? 'on' : ''} onClick={() => setTab(key)}>
            {t(`q.tab.${key}`)}
          </button>
        ))}
      </nav>

      <div class="toolbar">
        {/* Вердикты приходят от клиента таблицей, а не по одному: кнопка стоит
            в панели фильтров, потому что это работа со ВСЕЙ стопкой, а не с
            выделенными строками. */}
        {isManager && (
          <button type="button" class="btn-soft" onClick={() => setVerdictsOpen(true)}>
            {t('q.verdicts')}
          </button>
        )}
        <input
          type="search"
          placeholder={t('q.search')}
          value={q}
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
        />
        {contributors.length > 0 && (
          <select value={contributorId} onChange={(e) => setContributorId((e.target as HTMLSelectElement).value)}>
            <option value="">{t('q.allContributors')}</option>
            {contributors.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || c.email} ({c.n})
              </option>
            ))}
          </select>
        )}
        <div class="date-range">
          <span>{t('q.from')}</span>
          <input type="date" value={from} max={to || undefined} onInput={(e) => setFrom((e.target as HTMLInputElement).value)} />
          <span>{t('q.to')}</span>
          <input type="date" value={to} min={from || undefined} onInput={(e) => setTo((e.target as HTMLInputElement).value)} />
        </div>
      </div>

      {msg && <div class="strip info">{msg}</div>}

      {rows === null ? (
        <div class="empty">{res.loading ? t('q.loading') : t('q.loadFailed')}</div>
      ) : rows.length === 0 ? (
        <div class="empty">{t('q.empty')}</div>
      ) : (
        <>
          <div class="row actions">
            <span class="muted">{t('q.selected', { n: selected.size })}</span>
            {(canMoveFrom[tab] ?? [])
              .filter((a) => !a.managerOnly || isManager)
              .map((a) => (
                <button
                  key={a.move}
                  type="button"
                  class="btn-soft"
                  disabled={busy || selected.size === 0}
                  onClick={() => void move(a.move)}
                >
                  {a.label}
                </button>
              ))}
            {isManager && tab !== 'rejected' && tab !== 'accepted' && (
              <button
                type="button"
                class="btn-danger"
                disabled={busy || selected.size === 0}
                onClick={() => setRejectFor([...selected])}
              >
                {t('q.move.reject')}
              </button>
            )}
          </div>

          <div class="tablewrap">
            <table>
              <thead>
                <tr>
                  <th />
                  <th>{t('q.th.contributor')}</th>
                  <th>{t('q.th.topic')}</th>
                  <th>{t('q.th.length')}</th>
                  <th>{t('q.th.size')}</th>
                  <th>{t('q.th.source')}</th>
                  <th>{t('q.th.integrity')}</th>
                  <th>{t('q.th.filmed')}</th>
                  {/* QA до вердикта клиента (Tim 11.08, kids-парность): штаб
                      пересматривает отгруженное сразу, не дожидаясь ответа —
                      для этого нужно видеть, КОГДА клип уехал, отдельно от
                      «Снято». */}
                  {SHIPPED_DATE_TABS.has(tab) && <th>{t('q.th.shipped')}</th>}
                  <th>{t('q.th.files')}</th>
                  {(tab === 'rejected' || tab === 'quarantine') && <th>{t('q.th.reason')}</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggle(r.id)}
                      />
                    </td>
                    <td>
                      {r.contributorName ?? r.contributorEmail}
                      {r.ownerName && <span class="sub">{r.ownerName}</span>}
                    </td>
                    <td>{r.taskSlug ?? <span class="muted">—</span>}</td>
                    <td>{fmtDur(r.durationSeconds)}</td>
                    <td>{fmtMB(r.bytes)}</td>
                    {/* Загруженный файл гейт целостности не проходит — кадры
                        считать не по чему. Показываем источник рядом, чтобы
                        прочерк не читался как «проверка не отработала». */}
                    <td>
                      {r.source === 'upload' ? (
                        <span class="status-chip pend">{t('q.source.upload')}</span>
                      ) : (
                        <span class="muted">{t('q.source.recorder')}</span>
                      )}
                      {/* F10: устройство, чтобы штаб мог сопоставить брак
                          (смаз/дропы) с конкретным телефоном, а не с человеком. */}
                      {r.device && <span class="sub">{r.device}</span>}
                    </td>
                    <td>
                      {r.integrityRatio == null
                        ? r.source === 'upload'
                          ? <span class="muted">{t('q.integrity.na')}</span>
                          : '—'
                        : `${Math.round(r.integrityRatio * 100)}%`}
                    </td>
                    <td>{fmtDate(r.recordedAt ?? r.uploadedAt)}</td>
                    {SHIPPED_DATE_TABS.has(tab) && <td>{fmtDate(r.deliveredAt)}</td>}
                    <td class="files">
                      {hasPlayableFile(r) && (
                        <button type="button" class="link" onClick={() => setPlayFor(r)}>
                          {t('q.watch')}
                        </button>
                      )}
                      <a href={api.captureFileUrl(r.id, 'raw')} download>
                        {t('q.file.raw')}
                      </a>
                      {/* Немой клип в вивере — это либо «звук ещё сводится»,
                          либо «его и не было». Разница важная: в первом случае
                          ждать, во втором ждать нечего. */}
                      {r.audio === 'pending' && <span class="muted">{t('q.audio.pending')}</span>}
                      {r.audio === 'none' && <span class="muted">{t('q.audio.none')}</span>}
                      {r.hasVoiced && (
                        <a href={api.captureFileUrl(r.id, 'voiced')} download>
                          {t('q.file.voiced')}
                        </a>
                      )}
                      {tab === 'needs_voiceover' && (
                        <label class="link">
                          {t('q.uploadVoiced')}
                          <input
                            type="file"
                            accept="video/mp4"
                            hidden
                            onChange={(e) => {
                              const f = (e.target as HTMLInputElement).files?.[0];
                              if (f) void uploadVoiced(r.id, f);
                            }}
                          />
                        </label>
                      )}
                    </td>
                    {(tab === 'rejected' || tab === 'quarantine') && (
                      <td class="reason">
                        {r.rejectReason ?? '—'}
                        {r.diag && <span class="sub">{fmtDiag(r.diag)}</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {rejectFor && (
        <RejectDialog
          t={t}
          count={rejectFor.length}
          busy={busy}
          onCancel={() => setRejectFor(null)}
          onSubmit={(reason, note) => void move('reject', reason, note)}
        />
      )}

      {playFor && (
        <PlayerModal
          captureId={playFor.id}
          title={playFor.taskSlug ?? playFor.contributorName ?? playFor.contributorEmail}
          srcRaw={api.captureFileUrl(playFor.id, 'raw')}
          srcVoiced={playFor.hasVoiced ? api.captureFileUrl(playFor.id, 'voiced') : null}
          rawLabel={t('q.file.raw')}
          voicedLabel={t('q.file.voiced')}
          closeLabel={t('q.close')}
          preparingLabel={t('player.preparing')}
          onClose={() => setPlayFor(null)}
        />
      )}

      {verdictsOpen && (
        <VerdictsModal
          t={t}
          onClose={() => setVerdictsOpen(false)}
          onApplied={() => {
            invalidate('captures:');
            void load(tab);
          }}
        />
      )}
    </>
  );
}

function RejectDialog({
  t,
  count,
  busy,
  onCancel,
  onSubmit,
}: {
  t: T;
  count: number;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (reason: VistaRejectReason, note?: string) => void;
}) {
  const [reason, setReason] = useState<VistaRejectReason>('weak_narration');
  const [note, setNote] = useState('');
  return (
    <dialog open>
      <h3>{t('q.reject.title', { count })}</h3>
      {/* Причина обязательна: человек, которому не заплатили за съёмку, должен
          узнать почему — иначе он повторит ту же ошибку и мы заплатим дважды
          за обучение. */}
      <label class="field">
        <span>{t('q.th.reason')}</span>
        <select value={reason} onChange={(e) => setReason((e.target as HTMLSelectElement).value as VistaRejectReason)}>
          {REJECT_REASONS.map((k) => (
            <option key={k} value={k}>
              {t(`q.reason.${k}`)}
            </option>
          ))}
        </select>
      </label>
      <label class="field">
        <span>{t('q.comment')}</span>
        <input value={note} onInput={(e) => setNote((e.target as HTMLInputElement).value)} />
      </label>
      <div class="row">
        <button type="button" class="btn-danger" disabled={busy} onClick={() => onSubmit(reason, note || undefined)}>
          {t('q.move.reject')}
        </button>
        <button type="button" class="btn-ghost" onClick={onCancel}>
          {t('q.cancel')}
        </button>
      </div>
    </dialog>
  );
}
